import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Heart, ChatCircle, Trash } from "@phosphor-icons/react";

/**
 * Panel for likes + comments on any content type (news, releases, events).
 * entityType: "news" | "releases" | "events"
 * entityId: string
 */
export default function InteractionsPanel({ entityType, entityId }) {
  const { user, refresh } = useAuth();
  const [likeCount, setLikeCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get(`/${entityType}/${entityId}/interactions`);
        if (ignore) return;
        setLikeCount(data.like_count);
        setLiked(data.liked);
        setComments(data.comments);
      } catch (e) { /* silent */ }
    })();
    return () => { ignore = true; };
  }, [entityType, entityId]);

  async function toggleLike() {
    if (!user) { toast.error("Faz login para reagir"); return; }
    try {
      const { data } = await api.post(`/${entityType}/${entityId}/like`);
      setLiked(data.liked);
      setLikeCount(data.like_count);
      if (data.liked) refresh?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  async function submitComment(e) {
    e.preventDefault();
    if (!user) { toast.error("Faz login para comentar"); return; }
    const content = text.trim();
    if (!content) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/${entityType}/${entityId}/comments`, { content });
      setComments([...comments, data]);
      setText("");
      refresh?.();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  }

  async function deleteComment(commentId) {
    if (!window.confirm("Apagar este comentário?")) return;
    try {
      await api.delete(`/content-comments/${commentId}`);
      setComments(comments.filter(c => c.comment_id !== commentId));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  return (
    <div className="mt-12 pt-8 border-t border-white/10" data-testid="interactions-panel">
      {/* Like button + count */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={toggleLike}
          data-testid="like-button"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
            liked ? "bg-[#E63946] border-[#E63946] text-white" : "border-white/15 text-zinc-300 hover:border-[#E63946] hover:text-[#E63946]"
          }`}
        >
          <Heart size={18} weight={liked ? "fill" : "bold"} />
          <span className="font-display font-bold">{likeCount}</span>
          <span className="text-xs font-mono-accent uppercase tracking-wide">{liked ? "Gostas" : "Gosto"}</span>
        </button>
        <div className="flex items-center gap-2 text-zinc-400 text-sm">
          <ChatCircle size={16} weight="bold" />
          <span data-testid="comments-count">{comments.length} {comments.length === 1 ? "comentário" : "comentários"}</span>
        </div>
      </div>

      {/* Comment form */}
      {user ? (
        <form onSubmit={submitComment} className="mb-8" data-testid="comment-form">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Escreve um comentário..."
            rows={3}
            className="bg-[#121217] border-white/10 resize-none"
            data-testid="comment-input"
          />
          <div className="mt-3 flex justify-end">
            <Button type="submit" disabled={busy || !text.trim()} className="btn-crimson" data-testid="comment-submit-btn">
              {busy ? "A publicar..." : "Publicar"}
            </Button>
          </div>
        </form>
      ) : (
        <div className="mb-8 p-4 rounded-lg border border-dashed border-white/10 text-sm text-zinc-400 text-center">
          <Link to="/login" className="text-[#E63946] hover:underline">Faz login</Link> para comentar e ganhar XP.
        </div>
      )}

      {/* Comments list */}
      <div className="space-y-4" data-testid="comments-list">
        {comments.length === 0 && (
          <div className="text-center py-10 text-zinc-500 font-mono-accent text-xs">SEM COMENTÁRIOS AINDA</div>
        )}
        {comments.map((c) => {
          const canDelete = user && (user.user_id === c.user_id || user.role === "admin");
          const avatar = c.author_picture ? resolveImageUrl(c.author_picture) : null;
          return (
            <div key={c.comment_id} className="flex gap-3" data-testid={`comment-${c.comment_id}`}>
              {avatar ? (
                <img src={avatar} alt="" className="w-10 h-10 rounded-full object-cover border border-white/10 flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-sm font-display font-bold flex-shrink-0">
                  {(c.author_name || "?")[0]?.toUpperCase()}
                </div>
              )}
              <div className="flex-1 bg-[#121217] border border-white/5 rounded-xl px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-display font-semibold text-sm">{c.author_name || "Membro"}</div>
                  <div className="flex items-center gap-3">
                    <div className="font-mono-accent text-[10px] text-zinc-500">
                      {new Date(c.created_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" })}
                    </div>
                    {canDelete && (
                      <button onClick={() => deleteComment(c.comment_id)} className="text-zinc-500 hover:text-[#E63946]" title="Apagar" data-testid={`comment-delete-${c.comment_id}`}>
                        <Trash size={14} weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-1 text-zinc-300 text-sm whitespace-pre-line leading-relaxed">{c.content}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
